import { motion } from "framer-motion";
import { ShieldCheck, Headset, Wrench, Sparkle, ChartLineUp } from "@phosphor-icons/react";
import { useLang } from "../i18n/LangContext";
import { EditorialNumber } from "./EditorialNumber";

const revealUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.07, duration: 0.85, ease: [0.7, 0, 0.2, 1] },
  }),
};

const roleIcons = [ShieldCheck, Headset, Wrench, Sparkle, ChartLineUp];

const Card = ({ role, Icon, index }) => (
  <motion.div
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: "-40px" }}
    variants={revealUp}
    custom={index}
    className="relative bg-[#E6E3DC] p-8 md:p-10 clip-card group hover:bg-[#13535A] hover:text-[#F1EEE8] transition-colors duration-500 flex flex-col h-full"
    data-testid={`role-${role.n}`}
  >
    <div className="flex items-center justify-between mb-6">
      <span className="text-[0.62rem] tracking-[0.32em] uppercase text-[#8A6F45] font-medium">
        {role.n} · {role.label}
      </span>
      <Icon size={22} weight="light" className="text-[#8A6F45]" />
    </div>
    <h3 className="font-display text-2xl md:text-[1.7rem] tracking-tight leading-[1.15] mb-5 whitespace-pre-line">
      {role.title}
      <span className="text-[#8A6F45]">.</span>
    </h3>
    <p className="leading-relaxed text-[0.98rem] opacity-80 group-hover:opacity-90 mt-auto">
      {role.body}
    </p>
  </motion.div>
);

export const Protocol = () => {
  const { t } = useLang();
  const roles = t.protocol.roles;
  const topRow = roles.slice(0, 3);
  const bottomRow = roles.slice(3, 5);
  return (
    <section
      id="protocol"
      className="relative bg-[#F1EEE8] text-[#232326] py-28 md:py-40 overflow-hidden"
      data-testid="protocol-section"
    >
      {/* Ambient luxury background — architectural detail on the lake (columns, patterned floor) */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <img
          src="https://images.unsplash.com/photo-1642494510670-08a2e9c0bc13?auto=format&fit=crop&w=2400&q=90"
          alt=""
          className="w-full h-full object-cover object-center bg-ken-burns"
          style={{ filter: "contrast(1.02) brightness(0.94) saturate(0.92)" }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(241,238,232,0.86) 0%, rgba(241,238,232,0.80) 50%, rgba(241,238,232,0.90) 100%)",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at 50% 40%, rgba(241,238,232,0.0) 40%, rgba(35,35,38,0.12) 100%)",
          }}
        />
      </div>

      <EditorialNumber n="02" variant="dark" align="left" />
      <div className="relative max-w-[1600px] mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="grid md:grid-cols-12 gap-8 mb-16 md:mb-24">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={revealUp}
            className="md:col-span-3"
          >
            <div className="flex items-center gap-3 text-[#13535A] text-[0.7rem] tracking-[0.28em] uppercase font-semibold">
              <span className="w-6 h-px bg-[#8A6F45]" />
              <span>{t.protocol.overline}</span>
            </div>
          </motion.div>
          <div className="md:col-span-9">
            <motion.h2
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={revealUp}
              custom={1}
              className="font-display font-medium leading-[1.02] tracking-tight text-[clamp(2.2rem,5.4vw,4.8rem)] whitespace-pre-line"
            >
              {t.protocol.title}
            </motion.h2>
            <motion.p
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={revealUp}
              custom={2}
              className="text-[#232326]/70 text-lg leading-relaxed max-w-2xl mt-6"
            >
              {t.protocol.lead}
            </motion.p>
          </div>
        </div>

        {/* Cards grid: 3 + 2 (centered) */}
        <div className="border-t border-[#232326]/10 pt-14 space-y-6 md:space-y-8">
          {/* Top row: 3 cards */}
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {topRow.map((r, i) => (
              <Card key={r.n} role={r} Icon={roleIcons[i]} index={i} />
            ))}
          </div>
          {/* Bottom row: 2 cards centered */}
          <div className="grid md:grid-cols-6 gap-6 md:gap-8">
            <div className="hidden md:block md:col-span-1" />
            {bottomRow.map((r, i) => (
              <div key={r.n} className="md:col-span-2">
                <Card role={r} Icon={roleIcons[i + 3]} index={i + 3} />
              </div>
            ))}
            <div className="hidden md:block md:col-span-1" />
          </div>
        </div>
      </div>
    </section>
  );
};
